import first from 'lodash/first';
import { useMutation } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import { MutationError } from '@atlassian/jira-business-error-handling';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ValidationError } from '@atlassian/jira-fetch';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	TransitionAndRankJWMBoardView,
	TransitionAndRankJWMBoardViewVariables,
	TransitionAndRankJWMBoardView_transitionAndRankJwmItems_errors as TransitionAndRankError,
	// @ts-expect-error - TS2305 - Module '"./__generated_apollo__/TransitionAndRankJWMBoardView"' has no exported member 'JWMRankItemInput'.
	JWMRankItemInput,
} from './__generated_apollo__/TransitionAndRankJWMBoardView';
import { TRANSITION_AND_RANK_MUTATION } from './gql';
import type { TransitionAndRankInput, UseTransitionAndRankReturn } from './types';

const QUERY_NAME = 'transitionAndRankBoardView';

const isClientSideError = (error: TransitionAndRankError) => {
	const statusCode = error.extensions?.statusCode;

	return statusCode && statusCode >= 400 && statusCode < 500;
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useTransitionAndRank = (): UseTransitionAndRankReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const doFireErrorAnalytics = (error: any) => {
		fireErrorAnalytics({
			meta: {
				id: QUERY_NAME,
				packageName: 'jiraWorkManagementBoard',
				teamName: 'wanjel',
			},
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			error: Array.isArray(error) && error.length > 0 ? error[0] : (error as ApolloError),
			attributes: { message: 'Failed to transition and rank' },
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const [transitionAndRankMutation, { loading }] = useMutation<
		TransitionAndRankJWMBoardView,
		TransitionAndRankJWMBoardViewVariables
	>(TRANSITION_AND_RANK_MUTATION, {
		onCompleted: (onCompletedData) => {
			fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`, {});
			const errors = onCompletedData?.transitionAndRankJwmItems?.errors;
			const areAllErrorsClientSideErrors = errors?.every(isClientSideError);

			if (errors && errors.length > 0 && !areAllErrorsClientSideErrors) {
				doFireErrorAnalytics(
					onCompletedData.transitionAndRankJwmItems.errors.map((e) => new MutationError(e)),
				);
			}
		},
	});

	const transitionAndRank = async ({
		issueIds,
		transitionId,
		beforeIssueId,
		afterIssueId,
	}: TransitionAndRankInput) => {
		const queryInput: {
			issueIds: ReadonlyArray<number>;
			rank?: JWMRankItemInput;
			transitionId?: number;
		} = { issueIds };
		const rank =
			(beforeIssueId != null && { beforeIssueId }) || (afterIssueId != null && { afterIssueId });
		if (rank) {
			queryInput.rank = rank;
		}
		if (transitionId != null) {
			queryInput.transitionId = transitionId;
		}

		try {
			const result = await transitionAndRankMutation({
				variables: {
					input: queryInput,
				},
			});
			const error = first(result?.data?.transitionAndRankJwmItems?.errors);
			// Converting the mutation error to validation error which is ignored from experience error reporting
			// and displayed to the user properly with ErrorFlag component upstream.
			if (error) {
				throw new ValidationError(error?.message || '');
			}
		} catch (error) {
			doFireErrorAnalytics(error);

			throw error;
		}
	};

	return {
		transitionAndRank,
		loading,
	};
};
